// React imports
import React, { useState, useEffect, useRef } from 'react';

// Generic imports
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import _ from 'lodash';

// MUI imports
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faPaperclip } from '@fortawesome/pro-light-svg-icons';
import { faUserCog } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import email from '../utils/email.js';
import PRODUCT_TYPES from '../../ProductTypes.js';
import { findMatchingProducts, createEmptyProduct, migrateToAirFreight, migrateToLCL } from '../utils/product.js';
import { settleRoute } from '../utils/routes.js';
import { validIncoterm } from '../../utils/Incoterms.js';
import { emailHtmlToText, looksLikeHtml, pasteKeepingTables } from '../../utils/emailText.js';

// MUI imports

function EmailMagic(props) {
    const { onClose, accesstoken, org, setTransportationMethod, 
        setRoutes, setIncoterm, products, setProducts, seaPorts, unlocode, 
        me, setUpdates, updates, source, emailLoginInfo, accounts, setAccount } = props;
    const [ emailBody, setEmailBody ] = useState('');
    const [ emailSender, setEmailSender ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const [ aiGetInformationBase, setAiGetInformationBase ] = useState(null);
    const [ attachments, setAttachments ] = useState(updates?.[0]?.files || []);
    const [ file, setFile ] = useState(null);
    const [ adminOpen, setAdminOpen ] = useState(false);
    const [ aiMessage, setAiMessage ] = useState(null);
    const theme = useTheme();

    const setFileRef = useRef();
    setFileRef.current = setFile;
    const setAttachmentsRef = useRef();
    setAttachmentsRef.current = setAttachments;

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: async (acceptedFiles) => {
            setFileRef.current({ state: 'uploading' }); 

            const formData = new FormData();
            const file = acceptedFiles[0];
            formData.append('file', file, file.name);
            const { data } = await axios({
                url: `/api/opportunities/upload_file?organizationId=${org.id}&opportunityId=new&accesstoken=${accesstoken}`,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setAttachmentsRef.current(f => [...f, { url: data.location, name: file.name }]);
            setFileRef.current(null);
        },
    });

    useEffect(() => {
        if (me?.email === 'admin@monada.ai') {
            const { data: base } = axios.get(`/api/opportunities/ai-get-information-base?organizationId=${org.id}&accesstoken=${accesstoken}`).then(r => setAiGetInformationBase(r.data));
            setAiGetInformationBase(base);
        }
    }, [me]);

    // The model is handed the exact list of container types, but an email written as "40HC"
    // or "40' HQ" pulls it toward the customer's shorthand instead. Those used to fail an
    // exact name match and be dropped from the quote without a word, which reads as the
    // wizard simply not finding the containers.
    function resolveProductType({ type, transportationMethod }) {
        const options = PRODUCT_TYPES[transportationMethod] || [];
        // Compare on letters and digits alone, and treat HQ and High Cube as the HC they are.
        const canon = value => String(value || '')
            .toLowerCase()
            .replace(/high\s*cube/g, 'hc')
            .replace(/\bhq\b/g, 'hc')
            .replace(/[^a-z0-9]/g, '');

        const wanted = canon(type);
        if (!wanted) return null;

        // The exact name first: "Air Freight" (one line per package group) and "Air freight" (the
        // unified product, its packages in calculations) differ only in case, and the AI's flat
        // line must stay the first, which the form then folds into the unified product.
        if (options.some(o => o.name === type)) return type;
        const exact = options.find(o => canon(o.name) === wanted);
        if (exact) return exact.name;

        // A size and height with no box type named - "40' HC" - means a dry box.
        const dry = options.find(o => canon(o.name) === `${wanted}dry`);
        if (dry) return dry.name;

        // Otherwise accept it only if exactly one type could have been meant.
        const candidates = options.filter(o => canon(o.name).startsWith(wanted));
        return candidates.length === 1 ? candidates[0].name : null;
    }

    async function onDoIt() {
        setLoading(true);
        setAiMessage(null);
        try {
            let { data: response, headers: responseHeaders } = await axios.post(`/api/opportunities/ai-get-information?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                text: emailBody,
                base: aiGetInformationBase,
                attachments: attachments,
            });
            if (typeof response !== 'string') response = String(response || '');
            response = response.replaceAll('```javascript', '').replaceAll('```', '').trim();
            console.log(response);

            const isAddEmailCall = /addEmail\s*\(/.test(response);
            if (!isAddEmailCall) {
                setAiMessage({ text: response });
                setLoading(false);
                return;
            }

            window.addEmail = async (transportationMethod, routes, containers, incoterms) => {
                try {
                    console.log('[OpportunityOneOffEmailBuilder] AI RESULT', transportationMethod, routes, containers, incoterms);
                    const newRoutes = transportationMethod === 'sea' || transportationMethod === 'air' || transportationMethod === 'land' ?
                        await Promise.all(routes.map(async route => {
                            const [
                                { result: source, resultType: sourceType },
                                { result: destination, resultType: destinationType }
                             ] = await Promise.all([
                                settleRoute({ route: route.source, country: route.sourceCountry, unlocode, seaPorts, transportationMethod }),
                                settleRoute({ route: route.destination, country: route.destinationCountry, unlocode, seaPorts, transportationMethod }),
                            ]);

                            return {
                                source: source ? {
                                    text: `${source.code || source.iata_code ? `${source.code || source.iata_code} - ` : ''}${source.portname || source.name || source.text}`,
                                    countryCode: source.countryCode || source.iso_country || (source.code || source.portname).substr(0, 2),
                                    type: sourceType,
                                    id: source.iata_code || source.id || source.code,
                                } : null,
                                destination: destination ? {
                                    text: `${destination.code || destination.iata_code ? `${destination.code || destination.iata_code} - ` : ''}${destination.portname || destination.name || destination.text}`,
                                    countryCode: destination.countryCode || destination.iso_country || (destination.code || destination.portname).substr(0, 2),
                                    type: destinationType,
                                    id: destination.iata_code || destination.id || destination.code,
                                } : null,
                            }
                        })) : [];

                    console.log('[OpportunityOneOffEmailBuilder] newRoutes', newRoutes);
                    setTransportationMethod(transportationMethod);
                    setRoutes(newRoutes);
                    // Only a real incoterm: the AI may answer with "FCL" or the like when there is none.
                    const incoterm = validIncoterm(incoterms);
                    setIncoterm(incoterm);
                    // Map the shorthand onto a real type before deciding what to keep, and
                    // keep hold of whatever still cannot be placed so it can be reported
                    // rather than silently vanishing.
                    const resolvedContainers = (containers || []).map(c => ({
                        ...c,
                        type: resolveProductType({ type: c.type, transportationMethod }) || c.type,
                    }));
                    const unresolvedContainers = resolvedContainers
                        .filter(c => !PRODUCT_TYPES[transportationMethod].find(pt => pt.name === c.type));
                    const newProducts = resolvedContainers.filter(c => PRODUCT_TYPES[transportationMethod].find(pt => pt.name === c.type)).map(c => ({
                        ...createEmptyProduct({ transportationMethod }),
                        // The AI may emit type 'LCL' directly for LCL-by-W/M cargo. The unified LCL
                        // product needs calculations[], not flat weight/volume. Convert to a
                        // legacy WM type so migrateToLCL below folds it into the unified shape.
                        // Likewise a flat air line written as the unified "Air freight" becomes the
                        // per-line "Air Freight", so migrateToAirFreight builds its package lines.
                        type: c.type === 'LCL' ? (c.length && c.width && c.height ? 'By Dimensions or Weight (sea)' : 'By Volume or Weight (sea)') : c.type === 'Air freight' ? 'Air Freight' : c.type,
                        quantity: c.quantity,
                        ...(c.commodity ? { commodity: c.commodity } : {}),
                        ...(c.volume ? { volume: c.volume } : {}),
                        ...(c.weight ? { weight: c.weight } : {}),
                        ...(c.kgOrLbs ? { kgOrLbs: c.kgOrLbs } : {}),
                        ...(c.cmOrInches ? { cmOrInches: c.cmOrInches } : {}),
                        ...(c.length ? { length: c.length } : {}),
                        ...(c.height ? { height: c.height } : {}),
                        ...(c.width ? { width: c.width } : {}), 
                        ...(c.temperature ? { temperature: c.temperature } : {}),
                        ...(c.celsiusOrFahrenheit ? { celsiusOrFahrenheit: c.celsiusOrFahrenheit } : {}),
                        ...(c.chargeableWeight ? { chargeableWeight: c.chargeableWeight } : {}),
                        ...(c.dangerous ? { dangerous: c.dangerous } : {}),
                        ...(c.dangerousMSDS ? { dangerousMSDS: c.dangerousMSDS } : {}),
                        ...(c.dangerousClass ? { dangerousClass: c.dangerousClass } : {}),
                        ...(c.dangerousUNCode ? { dangerousUNCode: c.dangerousUNCode } : {}),
                    }));

                    // The form creates an empty Air freight or LCL product on its own when the mode is
                    // air or LCL. It is replaced by the AI's cargo, never compared with it: as a
                    // "match" it used to drop one of the AI's lines and leave an empty package line.
                    const isEmptyUnified = p => ['Air freight', 'LCL'].includes(p.type)
                        && (p.calculations || []).every(c => !c.weight && !c.volume && !c.length);
                    const kept = newProducts.length ? products.filter(p => !isEmptyUnified(p)) : products;
                    kept.forEach(p => {
                        const matchingProduct = _.first(findMatchingProducts({ product: p, products: newProducts }));
                        if (!matchingProduct) return;
                        _.pull(newProducts, matchingProduct);
                    })
                    // Collapse legacy 'Air Freight' / WM products into unified Air freight / LCL shape
                    const merged = migrateToAirFreight(migrateToLCL({ products: [...kept, ...newProducts] })).products;
                    setProducts(merged);

                    // Say so rather than quietly leaving them out - a container the wizard
                    // could not place is the difference between a complete quote and one
                    // that looks like it worked.
                    if (unresolvedContainers.length > 0) {
                        setAiMessage({ text: `Could not match ${unresolvedContainers.length === 1 ? 'this container type' : 'these container types'} to a product: ${unresolvedContainers.map(c => c.type).join(', ')}. Please add ${unresolvedContainers.length === 1 ? 'it' : 'them'} manually.` });
                    }

                    const newUpdates = _.cloneDeep(updates || []);
                    newUpdates[0] = {
                        ...newUpdates[0],
                        createdBy: me?.email,
                        created: new Date().getTime(),
                        html: `
                            <div style="font-size: 11px;">
                                <p>We've automatically filled out the opportunity information based on the email you provided.</p>
                                <pre style="margin-left: 12px; font-family: monospace;">${emailBody.trim()}</pre>
                                <p>Response from our AI:</p>
                                <ul>
                                    <li>Sender: ${escape(emailSender)}</li>
                                    <li>Transportation method: ${transportationMethod}</li>
                                    <li>Routes: ${JSON.stringify(routes)}</li>
                                    <li>Products: ${JSON.stringify(newProducts)}</li>
                                </ul>
                            </div>
                        `,
                        // What the AI read and what it put in the form, kept as it was: a later
                        // change to these shipment details is recorded as a correction
                        // (aiParseDiff.js; docs/inquiry-agent/README.md).
                        aiParse: {
                            created: new Date().getTime(),
                            from: source?.type === 'email' ? 'mailbox' : 'pasted',
                            model: responseHeaders?.['x-ai-model'] || null,
                            text: emailBody.trim().slice(0, 50000),
                            answer: response.slice(0, 20000),
                            filled: { transportationMethod, incoterm, routes: newRoutes, products: merged },
                        },
                    }
                    setUpdates({ updates: newUpdates });
                } catch (e) {
                    console.log(e);
                }
            };
            try {
                eval(response);
                window.addEmail = null;
                onClose();
            } catch (e) {
                console.log(e);
                setAiMessage({ text: response });
            }
        } catch (e) {
            console.log(e);
            setAiMessage({ text: (typeof e?.response?.data === 'string' ? e.response.data : e?.response?.data?.detail) || e?.message || 'An error occurred while contacting the AI.' });
        }
        setLoading(false);
    }

    useEffect(() => {
        (async () => {
            if (source?.type === 'email' && emailLoginInfo?.tokens && !emailBody) {
                try {
                    const messages = await email.getMessagesForConversation({ org, conversationId: source.id, tokens: emailLoginInfo.tokens });
                    if (!messages || messages.length === 0) {
                        return;
                    }
                    
                    const sorted = _.orderBy(messages, m => new Date(m.receivedDateTime).getTime(), 'desc');
                    const latest = sorted[0];

                if (latest) {
                    const toRecipient = _.find([
                        latest.sender?.emailAddress,
                        ...latest.toRecipients?.map(r => r.emailAddress)
                    ], a => a?.address !== emailLoginInfo.email) || emailLoginInfo.email;

                    const account = _.find(accounts, a => _.some(a.contacts, c => c.email === toRecipient.address));
                    if (account) {
                        setAccount({
                            ...account,
                            email: toRecipient.address,
                        });
                    }

                    if (latest.sender?.emailAddress?.name) {
                        setEmailSender(`${latest.sender?.emailAddress?.name} <${latest.sender?.emailAddress?.address}>`);
                    } else if (latest.sender?.emailAddress?.address) {
                        setEmailSender(latest.sender?.emailAddress?.address);
                    }

                    // Concatenate all messages (newest first) for full thread context
                    const allBodiesText = sorted.map(msg => {
                        const senderName = msg.sender?.emailAddress?.name || '';
                        const senderAddr = msg.sender?.emailAddress?.address || '';
                        const from = senderName ? `${senderName} <${senderAddr}>` : senderAddr;
                        // Keeps tables as rows with " | " between cells (textContent glued them).
                        const content = msg.body?.content || '';
                        const bodyText = msg.body?.contentType === 'text' || !looksLikeHtml(content) ? content : emailHtmlToText(content);
                        return `From: ${from}\n${bodyText}`;
                    }).join('\n---\n');
                    setEmailBody(allBodiesText);
                }
                } catch (e) {
                    // Check if this is an expired token error
                    const errorMessage = e?.message || e?.error?.message || JSON.stringify(e);
                    const isExpiredError = errorMessage?.includes('Lifetime validation failed') || 
                                          errorMessage?.includes('expired') ||
                                          errorMessage?.includes('InvalidAuthenticationToken') ||
                                          e?.statusCode === 401 ||
                                          e?.code === 'InvalidAuthenticationToken';
                    
                    if (isExpiredError) {
                        // Token expired - silently handle, the expired listener will be called by getMessagesForConversation
                        console.log('[OpportunityOneOffEmailBuilder] Token expired when fetching messages');
                    } else {
                        console.error('[OpportunityOneOffEmailBuilder] Error fetching messages', e);
                    }
                }
            }
        })();
    }, [source, emailLoginInfo?.status]);

    if (source?.type === 'email' && emailLoginInfo?.tokens && !emailBody) {
        return (
            <Dialog open={true} onClose={onClose} fullWidth maxWidth='md' disableRestoreFocus={true}>
                <DialogTitle>
                    Email Magic
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                        Loading Email...
                    </Box>
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth='md' disableRestoreFocus={true}>
            <DialogTitle>
                Email Magic
            </DialogTitle>
            <DialogContent>
                <Box>
                    Enter an email you've received below, and we will magically fill out the opportunity information for you.
                </Box>
                <TextField
                    fullWidth
                    label='Email'
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    onPaste={e => pasteKeepingTables(e, setEmailBody)}
                    margin='normal'
                    multiline
                    rows={8}
                />
                <Box>
                    <Box sx={{ mt: 2 }}>
                        Attachments:
                    </Box>
                    {attachments && attachments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            {attachments.map((file, index) => (
                                <Chip
                                    onDelete={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setAttachments(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    key={index}
                                    label={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: '4px' }} />
                                        {file.name}
                                    </Box>}
                                    component="a"
                                    href={file.url}
                                    target="_blank"
                                    clickable
                                    sx={{ mr: 1, mb: 1 }}
                                />
                            ))}
                        </Box>
                    )}
                    <Box sx={{ marginTop: '12px', padding: '12px', textAlign: 'center', border: `1px solid ${theme.palette.border1}`, boxShadow: '1px 1px 1px rgba(0,0,0,0.4)', cursor: 'pointer' }}>
                        {file === null && (
                            <div {...getRootProps()}>
                                <FontAwesomeIcon style={{ fontSize: '48px' }} icon={faCloudArrowUp} />
                                <input {...getInputProps()} />
                                <Typography variant='caption' component={Box} >Drag and drop files here or click to browse.</Typography>
                            </div>
                        )}
                        {file !== null && file.state === 'uploading' && (
                            <Box sx={{ textAlign: 'center' }}>
                                <CircularProgress size={18} />
                            </Box>
                        )}
                    </Box>
                </Box>
                {me?.email === 'admin@monada.ai' && (
                    <IconButton onClick={() => setAdminOpen(!adminOpen)} sx={{ marginTop: '12px' }}>
                        <FontAwesomeIcon icon={faUserCog} />
                    </IconButton>
                )}
                {me?.email === 'admin@monada.ai' && adminOpen && (
                    <>
                        <Box>
                            DEBUG ADMIN ONLY - Edit the ChatGPT base here:
                        </Box>
                        <TextField
                            fullWidth
                            label='ChatGPT Base (loading...)'
                            value={aiGetInformationBase}
                            onChange={(e) => setAiGetInformationBase(e.target.value)}
                            margin='normal'
                            multiline
                            rows={8}
                        />
                    </>
                )}
                {aiMessage && (
                    <Alert severity="info" onClose={() => setAiMessage(null)} sx={{ mt: 2 }}>
                        <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{aiMessage.text}</Box>
                    </Alert>
                )}
                <Box sx={{ padding: '0px 20%', mt: 3 }}>
                    <Button color='primary' variant='contained' onClick={onDoIt} fullWidth disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Do It!'}
                    </Button>
                </Box>
                <Box sx={{ padding: '0px 40%', marginTop: '12px' }}>
                    <Button color='primary' variant='text' onClick={onClose} fullWidth sx={{ textTransform: 'none' }}>Maybe later</Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default EmailMagic;